import { Box, Card, Group, Loader, SegmentedControl, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { SettingsTabs } from '../../components/app-shell/SettingsTabs';
import { MotionSection, PageHero, SectionTitle } from '../../components/ui';
import { csrfHeaders, useAuth } from '../../lib/auth-context';
import { type ColorSchemePreference, useColorSchemePreference } from '../../lib/color-scheme';
import { showToast } from '../../lib/toast';

export const Route = createFileRoute('/settings/appearance')({
  component: AppearanceSettingsPage,
});

function AppearanceSettingsPage() {
  const { user, isLoading, refresh } = useAuth();
  const { apply } = useColorSchemePreference();
  const [value, setValue] = useState<ColorSchemePreference>(
    user?.colorSchemePreference ?? 'dark',
  );

  const mutation = useMutation({
    mutationFn: async (colorScheme: ColorSchemePreference) => {
      const res = await fetch('/api/users/me/appearance', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({ colorScheme }),
      });
      if (!res.ok) throw new Error('Save failed');
    },
    onSuccess: () => {
      refresh();
    },
    onError: () => {
      showToast({ kind: 'error', body: 'Failed to save preference. Try again shortly.' });
      setValue(user?.colorSchemePreference ?? 'dark');
      apply(user?.colorSchemePreference ?? 'dark');
    },
  });

  const handleChange = (next: string) => {
    const pref = next as ColorSchemePreference;
    setValue(pref);
    apply(pref);
    mutation.mutate(pref);
  };

  if (isLoading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  if (!user) {
    return (
      <AppShell tone="form">
        <Text>Sign in to manage your settings.</Text>
      </AppShell>
    );
  }

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Settings"
            title="Appearance"
            lede="Choose how WWAAVE looks on your device."
          />
        </MotionSection>

        <SettingsTabs current="appearance" />

        <Card withBorder padding="lg" radius="md">
          <SectionTitle title="Colour scheme" order={3} />
          <Text size="sm" c="dimmed" mt="xs" mb="md">
            Select your preferred colour scheme. System follows your device settings.
          </Text>
          <SegmentedControl
            value={value}
            onChange={handleChange}
            data={[
              { label: 'Light', value: 'light' },
              { label: 'Dark', value: 'dark' },
              { label: 'System', value: 'system' },
            ]}
            fullWidth
          />
        </Card>

        <SiteBrandingCard />
      </Stack>
    </AppShell>
  );
}

// ─── Site branding (read-only) ──────────────────────────────────────────────

function SiteBrandingCard() {
  const branding = useMemo(() => {
    if (typeof document === 'undefined') return null;
    try {
      const el = document.getElementById('__wwaave_branding_data__');
      if (el?.textContent) {
        return JSON.parse(el.textContent) as {
          primaryHex?: string;
          accentHex?: string;
          font?: string;
        };
      }
    } catch { /* fall through */ }
    return null;
  }, []);

  const primary = branding?.primaryHex ?? '#d2602e';
  const accent = branding?.accentHex ?? '#e9a23b';
  const font = branding?.font ?? 'Hanken Grotesk';

  return (
    <Card withBorder padding="lg" radius="md">
      <SectionTitle title="Site branding" order={3} />
      <Text size="sm" c="dimmed" mt="xs" mb="md">
        These colours and font are set site-wide by the WWAAVE team.
      </Text>
      <Group gap="lg">
        <Group gap={8}>
          <Box
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              backgroundColor: primary,
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          />
          <Box
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              backgroundColor: accent,
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          />
          <Box
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: `linear-gradient(135deg, ${primary}, ${accent})`,
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          />
        </Group>
        <Text size="sm" c="dimmed">
          {font}
        </Text>
      </Group>
    </Card>
  );
}
